import { useRef, useState } from "react";
import { Bookmark, Download, FileImage, Link2, LockKeyhole, UploadCloud } from "lucide-react";
import { createUrlMap, resolveSource, uploadMap } from "../api";
import type { MapRecord, SourceChoice } from "../types";

interface CustomImportProps { onCreated: (map: MapRecord) => void }

export function CustomImport({ onCreated }: CustomImportProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [url, setUrl] = useState("");
  const [sources, setSources] = useState<SourceChoice[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function handleFile(file?: File) {
    if (!file) return;
    setBusy(true); setError(""); setSources([]);
    try { onCreated(await uploadMap(file)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Upload fehlgeschlagen"); }
    finally { setBusy(false); }
  }

  async function inspectUrl() {
    if (!url.trim()) return;
    setBusy(true); setError(""); setSources([]);
    try {
      const resolved = await resolveSource(url.trim());
      if (resolved.sources.length === 1) await startUrl(resolved.sources[0]);
      else setSources(resolved.sources);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Link konnte nicht geladen werden"); }
    finally { setBusy(false); }
  }

  async function startUrl(source: SourceChoice) {
    setBusy(true); setError("");
    try { onCreated(await createUrlMap(source.url)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Karte konnte nicht geladen werden"); }
    finally { setBusy(false); }
  }

  return (
    <section className="custom-import-section" id="eigene-karte" aria-labelledby="custom-import-title">
      <div className="section-intro">
        <span className="section-kicker"><LockKeyhole size={14} /> Eigener Import</span>
        <h2 id="custom-import-title">Du hast schon ein PDF oder Bild?</h2>
        <p>Lade eine eigene Karte hoch oder füge den Link zu einer Hofflohmarkt-Seite ein.</p>
      </div>
      <div className="custom-import-grid">
        <section
          className={`dropzone ${dragging ? "is-dragging" : ""}`}
          onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
          onDrop={(event) => { event.preventDefault(); setDragging(false); void handleFile(event.dataTransfer.files[0]); }}
          aria-label="Eigene Karte hochladen"
        >
          <div className="upload-icon"><UploadCloud size={27} /></div>
          <h3>{dragging ? "Einfach hier loslassen" : "Karte hier ablegen"}</h3>
          <p>PDF, PNG, JPG oder WebP · maximal 25 MB</p>
          <input ref={inputRef} type="file" hidden accept="application/pdf,image/png,image/jpeg,image/webp" onChange={(event) => void handleFile(event.target.files?.[0])} />
          <button className="button button-primary" onClick={() => inputRef.current?.click()} disabled={busy}>
            <FileImage size={18} /> {busy ? "Wird geladen …" : "Datei auswählen"}
          </button>
          <div className="or-divider"><span>ODER PER LINK</span></div>
          <div className="url-row">
            <span className="input-icon"><Link2 size={17} /></span>
            <input value={url} onChange={(event) => setUrl(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void inspectUrl(); }} placeholder="Link zur Übersichtsseite oder Karten-PDF" aria-label="Link zur Hofflohmarkt-Karte" />
            <button className="button button-secondary" onClick={() => void inspectUrl()} disabled={busy || !url.trim()}>Laden</button>
          </div>
          {error && <div className="inline-error" role="alert">{error}</div>}
          {sources.length > 1 && (
            <div className="source-picker">
              <div className="source-picker-head"><strong>{sources.length} Tourpläne gefunden</strong><span>Bitte auswählen</span></div>
              <div className="source-list">
                {sources.map((source) => <button key={source.url} onClick={() => void startUrl(source)}><span className="source-file"><FileImage size={16} /></span><span>{source.label}</span><Download size={16} /></button>)}
              </div>
            </div>
          )}
        </section>
        <aside className="link-retention-note">
          <span className="retention-icon"><Bookmark size={21} /></span>
          <div><strong>Bitte Link speichern</strong><p>Eigene Importe erscheinen in keiner persönlichen Liste. Die Karte bleibt 30 Tage verfügbar – jeder spätere Aufruf des Links verlängert die Frist erneut.</p></div>
        </aside>
      </div>
    </section>
  );
}
